import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Divider,
  Modal,
  Space,
  Table,
  Typography,
  message,
} from "antd";

const { Title, Text, Link } = Typography;

const capitalizeStatus = (status) => {
  if (!status) {
    return "-";
  }

  return String(status)
    .replace(/[_\s]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
};

const CHARGEABLE_SUBSCRIPTION_STATUSES = new Set([
  "active",
  "trialing",
  "past_due",
  "unpaid",
]);

const isChargeableStatus = (status) => {
  if (!status) {
    return false;
  }

  return CHARGEABLE_SUBSCRIPTION_STATUSES.has(String(status).toLowerCase());
};

const resolveErrorMessage = (error) => {
  if (!error) {
    return "Unable to cancel subscription.";
  }

  const responseData = error?.response?.data;
  if (responseData) {
    if (typeof responseData === "string" && responseData.trim()) {
      return responseData;
    }
    if (
      typeof responseData?.message === "string" &&
      responseData.message.trim()
    ) {
      return responseData.message;
    }
    if (typeof responseData?.error === "string" && responseData.error.trim()) {
      return responseData.error;
    }
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message;
  }

  return "Unable to cancel subscription.";
};

const PENDING_CANCELLATION_STORAGE_KEY = "billing.pendingCancellationPatch";

const BillingStatusView = ({
  summary,
  invoices,
  invoicesLoading,
  invoicesError,
  onReloadInvoices,
  formatCurrency,
  onCancelSubscription,
}) => {
  const columns = useMemo(
    () => [
      {
        title: "Invoice Number",
        dataIndex: "invoiceNumber",
        key: "invoiceNumber",
      },
      {
        title: "Invoice Date",
        dataIndex: "invoiceDate",
        key: "invoiceDate",
        render: (value) => value || "-",
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        render: (_, record) =>
          formatCurrency(record.amount, record.currency ?? summary?.currency),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (value) => capitalizeStatus(value),
      },
      {
        title: "Paid Date",
        dataIndex: "paidDate",
        key: "paidDate",
        render: (value) => value || "-",
      },
      {
        title: "Invoice",
        key: "invoice",
        render: (_, record) =>
          record.invoicePDFUrl ? (
            <Link
              href={record.invoicePDFUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              View PDF
            </Link>
          ) : (
            "-"
          ),
      },
    ],
    [formatCurrency, summary?.currency]
  );

  const [isCancelling, setIsCancelling] = useState(false);
  const [localCancellation, setLocalCancellation] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.sessionStorage) {
      return;
    }

    try {
      const raw = window.sessionStorage.getItem(
        PENDING_CANCELLATION_STORAGE_KEY
      );
      if (!raw) {
        return;
      }

      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") {
        window.sessionStorage.removeItem(PENDING_CANCELLATION_STORAGE_KEY);
        return;
      }

      if (summary?.subscriptionId && parsed.subscriptionId) {
        if (parsed.subscriptionId === summary.subscriptionId) {
          setLocalCancellation(parsed);
        } else {
          window.sessionStorage.removeItem(PENDING_CANCELLATION_STORAGE_KEY);
        }
      } else if (!summary?.subscriptionId) {
        setLocalCancellation(parsed);
      }
    } catch (storageError) {
      // ignore storage issues
    }
  }, [summary?.subscriptionId]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.sessionStorage) {
      return;
    }

    try {
      if (!localCancellation) {
        window.sessionStorage.removeItem(PENDING_CANCELLATION_STORAGE_KEY);
      } else {
        window.sessionStorage.setItem(
          PENDING_CANCELLATION_STORAGE_KEY,
          JSON.stringify(localCancellation)
        );
      }
    } catch (storageError) {
      // ignore storage issues
    }
  }, [localCancellation]);

  const effectiveSummary = useMemo(() => {
    if (!summary) {
      return null;
    }

    if (!localCancellation) {
      return summary;
    }

    if (
      localCancellation.subscriptionId &&
      summary.subscriptionId &&
      localCancellation.subscriptionId !== summary.subscriptionId
    ) {
      return summary;
    }

    return {
      ...summary,
      ...localCancellation,
    };
  }, [summary, localCancellation]);

  useEffect(() => {
    if (!summary) {
      if (localCancellation) {
        setLocalCancellation(null);
      }
      return;
    }

    const summaryStatus = String(summary.status ?? "").toLowerCase();
    if (
      localCancellation &&
      (summary.cancelAtPeriodEnd || summaryStatus === "canceled")
    ) {
      setLocalCancellation(null);
    }
  }, [summary, localCancellation]);

  const subscriptionId = effectiveSummary?.subscriptionId ?? null;
  const userCanCancel = effectiveSummary?.userCanCancel;
  const cancellationScheduled = effectiveSummary?.cancelAtPeriodEnd;
  const canceledAtLabel = effectiveSummary?.canceledAt || null;
  const nextChargeLabel = effectiveSummary?.nextBillingDate || null;
  const cancellationEffectiveDateValue =
    effectiveSummary?.cancellationEffectiveDate || null;
  const cancellationDateLabel = cancellationScheduled
    ? cancellationEffectiveDateValue || nextChargeLabel || null
    : null;
  const rawStatus = effectiveSummary?.status;
  const normalizedStatus =
    typeof rawStatus === "string" ? rawStatus.toLowerCase() : "";
  const statusIsCanceled = normalizedStatus === "canceled";
  const rawStatusLabel = capitalizeStatus(rawStatus);
  const subscriptionStatusLabel =
    cancellationScheduled && !statusIsCanceled
      ? `${rawStatusLabel} (Cancellation Scheduled)`
      : rawStatusLabel;
  const willAttemptToCharge = isChargeableStatus(rawStatus);
  const fallbackCancellationDate =
    cancellationEffectiveDateValue ?? nextChargeLabel ?? null;

  const disableCancelButton =
    !subscriptionId ||
    !userCanCancel ||
    cancellationScheduled ||
    statusIsCanceled ||
    !onCancelSubscription ||
    isCancelling;

  const cancelButtonLabel = statusIsCanceled
    ? "Canceled"
    : cancellationScheduled
    ? "Cancellation Scheduled"
    : "Cancel Service";

  const handleCancelClick = useCallback(() => {
    if (disableCancelButton) {
      if (!subscriptionId) {
        message.error("Subscription information is unavailable.", 5);
      }
      return;
    }

    Modal.confirm({
      title: "Cancel subscription",
      content:
        "Your subscription will remain active until the end of the current billing period. No refunds will be issued for this cancellation.",
      okText: "Confirm cancellation",
      cancelText: "Keep subscription",
      okButtonProps: { danger: true },
      onOk: async () => {
        setIsCancelling(true);
        try {
          await onCancelSubscription(subscriptionId);
          setLocalCancellation({
            subscriptionId,
            cancelAtPeriodEnd: true,
            cancellationEffectiveDate: fallbackCancellationDate,
          });
          message.success(
            "Cancellation scheduled for the end of your current billing period."
          );
        } catch (error) {
          message.error(resolveErrorMessage(error), 5);
          throw error;
        } finally {
          setIsCancelling(false);
        }
      },
    });
  }, [
    disableCancelButton,
    fallbackCancellationDate,
    onCancelSubscription,
    subscriptionId,
  ]);

  if (!effectiveSummary) {
    return null;
  }

  const totalAmountLabel = formatCurrency(
    effectiveSummary.totalAmount ?? 0,
    effectiveSummary.currency
  );
  const pricePerUserLabel =
    effectiveSummary.pricePerUser != null
      ? formatCurrency(effectiveSummary.pricePerUser, effectiveSummary.currency)
      : null;
  const shouldShowNextCharge =
    willAttemptToCharge &&
    Boolean(nextChargeLabel) &&
    !cancellationScheduled &&
    !statusIsCanceled;

  return (
    <div className="billing-status-view">
      <Title level={3}>Billing details</Title>
      <div className="billing-status-layout">
        <Card className="billing-summary-card">
          <Space direction="vertical" size="small">
            <Title level={2} style={{ marginBottom: 0 }}>
              {totalAmountLabel}
            </Title>
            {/* <Text>
              for {effectiveSummary.totalUsers} total{" "}
              {effectiveSummary.totalUsers === 1 ? "employee" : "employees"}
            </Text> */}
            <Text strong>{effectiveSummary.planName}</Text>
            <Divider style={{ margin: "12px 0" }} />
            <Text type="secondary">Status: {subscriptionStatusLabel}</Text>
            {shouldShowNextCharge && (
              <Text type="secondary">Next charge on {nextChargeLabel}</Text>
            )}
            {/* {shouldShowPricePerUser && (
              <Text type="secondary">{pricePerUserLabel} per employee</Text>
            )} */}
            <div className="billing-status-actions">
              <Button
                danger
                ghost
                onClick={handleCancelClick}
                disabled={disableCancelButton}
                loading={isCancelling}
              >
                {cancelButtonLabel}
              </Button>
              {effectiveSummary.userCanCancel === false && (
                <Text
                  type="secondary"
                  style={{ display: "block", marginTop: 8 }}
                >
                  Please contact support to cancel your subscription.
                </Text>
              )}
              {cancellationScheduled && (
                <Text
                  type="secondary"
                  style={{ display: "block", marginTop: 8 }}
                >
                  {cancellationDateLabel
                    ? `Cancellation scheduled for ${cancellationDateLabel}.`
                    : "Cancellation scheduled at the end of your current billing period."}{" "}
                  Your subscription remains active until then.
                </Text>
              )}
              {canceledAtLabel && !cancellationScheduled && (
                <Text
                  type="secondary"
                  style={{ display: "block", marginTop: 8 }}
                >
                  Subscription canceled on {canceledAtLabel}.
                </Text>
              )}
            </div>
          </Space>
        </Card>
        <div className="billing-invoices-section">
          <Title level={4} className="billing-invoices-title">
            Invoices
          </Title>
          {invoicesError && (
            <Alert
              className="billing-invoices-alert"
              type="error"
              showIcon
              message="Unable to load invoices"
              description={invoicesError?.message || "Please try again later."}
              action={
                onReloadInvoices ? (
                  <Button
                    size="small"
                    type="primary"
                    onClick={onReloadInvoices}
                  >
                    Retry
                  </Button>
                ) : null
              }
              style={{ marginBottom: 16 }}
            />
          )}
          <Table
            className="billing-status-table"
            columns={columns}
            dataSource={invoices}
            pagination={{ pageSize: 6, showSizeChanger: false }}
            rowKey={(record) => record.key || record.id || record.invoiceNumber}
            loading={invoicesLoading}
            locale={{ emptyText: invoicesLoading ? "" : "No invoices found" }}
          />
        </div>
      </div>
    </div>
  );
};

BillingStatusView.defaultProps = {
  summary: null,
  invoices: [],
  invoicesLoading: false,
  invoicesError: null,
  onReloadInvoices: null,
  onCancelSubscription: null,
  formatCurrency: (value) => value,
};

export default BillingStatusView;
